<script setup>
import { computed, useAttrs } from 'vue';

import { useTheme } from '../composables/useTheme';

/**
 * M3 tab bar. Pairs with HrTabPanels + HrTabPanel for content switching,
 * or with route-tab links for navigation.
 *
 * Two variants per M3 spec:
 *   primary   — underline indicator, default
 *   secondary — filled indicator, used inside a surface container
 *
 * v-model (and all other Quasar q-tabs props) flow through attrs.
 * Use legacyProps for migration-only concerns (align, dense,
 * active-color, indicator-color, narrow-indicator, etc.) — these
 * are ignored in theme mode where M3 token colors take over.
 *
 * @status recommended
 * @group navigation
 * @useWhen Building a tab bar for switching between content views with primary or secondary variant
 * @related HrTabPanels
 * @see https://quasar.dev/vue-components/tabs Quasar q-tabs API
 */
defineOptions({ name: 'HrTabs' });

const props = defineProps({
  /**
   * M3 tab variant.
   * @values primary, secondary
   */
  variant: {
    type: String,
    default: 'primary',
    validator: (v) => ['primary', 'secondary'].includes(v),
  },
  /**
   * Allow tabs to scroll horizontally when they overflow the bar width.
   * When false (default) tabs are fixed-width and fill the bar equally —
   * the M3 "fixed tabs" behavior.
   */
  scrollable: {
    type: Boolean,
    default: false,
  },
  /**
   * Legacy props forwarded to the underlying Quasar component (e.g. dense, active-color, indicator-color).
   * @deprecated Only for backwards compatibility with the legacy theme — ignored when a design system theme is active.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const attrs = useAttrs();
const { hasTheme } = useTheme();

const classes = computed(() => [
  'hr-tabs',
  `hr-tabs--${props.variant}`,
]);

// Reasonable Quasar approximations for each M3 variant.
// secondary has no clean Quasar equivalent so it gets the same defaults as
// primary; callers can override via legacyProps if they need a filled look.
const legacyVariantDefaults = {
  primary: { activeColor: 'primary', indicatorColor: 'primary', narrowIndicator: true },
  secondary: { activeColor: 'primary', indicatorColor: 'primary', narrowIndicator: true },
};

const effectiveProps = computed(() => {
  if (hasTheme.value) {
    return { narrowIndicator: props.variant === 'primary', align: props.scrollable ? undefined : 'justify', ...attrs };
  }
  return {
    ...legacyVariantDefaults[props.variant],
    align: props.scrollable ? undefined : 'justify',
    ...props.legacyProps,
    ...attrs,
  };
});
</script>

<template>
  <q-tabs
    v-bind="effectiveProps"
    :class="hasTheme ? classes : undefined"
  >
    <!-- @slot default HrTab (and HrBadge) components that make up the tab bar. -->
    <slot />
  </q-tabs>
</template>

<style lang="scss" scoped>

/* Only applies to light/dark themes */

.hr-tabs {
  border-bottom: 1px solid var(--app-outline-variant);
}

/* overflow:hidden on q-tabs__content is required for horizontal tab scrolling
   and cannot be split per-axis in CSS (overflow-x:hidden coerces overflow-y:visible
   to auto). The padding+margin trick extends the clipping boundary upward so
   badges on icon tabs are not clipped, while keeping tab content at the same
   visual position. */
.hr-tabs :deep(.q-tabs__content) {
  padding-top: 8px;
  margin-top: -8px;
}

.hr-tabs :deep(.q-tab) {
  min-height: 48px;
  padding: 0 16px;
  color: var(--app-on-surface-variant);
  font-size: var(--app-type-label-large-size);
  font-weight: var(--app-type-label-large-weight);
  letter-spacing: var(--app-type-label-large-tracking);
  text-transform: none;
}

.hr-tabs :deep(.q-tab--active) {
  color: var(--app-primary);
}

.hr-tabs :deep(.q-tab__indicator) {
  height: 3px;
  border-radius: 3px 3px 0 0;
  background: transparent;
}

.hr-tabs :deep(.q-tab--active .q-tab__indicator) {
  background: var(--app-primary);
  opacity: 1;
}

/* M3: primary active indicator is inset 2dp each side, min 24dp */
.hr-tabs--primary :deep(.q-tab--active .q-tab__indicator) {
  margin: 0 2px;
  min-width: 24px;
}

/* ── Secondary variant ───────────────────────────────────────── */
/*
 * The indicator fills the full tab height as a background highlight.
 * z-index: 0 pushes it behind the tab content (z-index: 1) so the
 * label renders above the colored fill.
 */
.hr-tabs--secondary :deep(.q-tab--active .q-tab__indicator) {
  height: 100%;
  top: 0;
  border-radius: var(--app-shape-corner-small) var(--app-shape-corner-small) 0 0;
  background: var(--app-secondary-container);
  z-index: 0;
}

.hr-tabs--secondary :deep(.q-tab__content) {
  position: relative;
  z-index: 1;
}

.hr-tabs--secondary :deep(.q-tab--active) {
  color: var(--app-on-secondary-container);
}

</style>
